'use client';
import React, { useState } from 'react';
import TicTacToeGame from './TicTacToeGame';
import MemoryMatchGame from './MemoryMatchGame';
import ChessGame from './ChessGame';
import SudokuGame from './SudokuGame';

type GameId = 'tictactoe' | 'memory' | 'chess' | 'sudoku' | null;

const GAMES = [
  {
    id: 'tictactoe' as GameId,
    title: 'Tic Tac Toe',
    emoji: '⭕',
    description: 'Classic X vs O strategy game. Get three in a row to win.',
    difficulty: 'Easy',
    players: '2 Players',
    color: '#7C3AED',
    glow: 'rgba(124,58,237,0.3)',
    tags: ['Strategy', 'Classic', 'Quick'],
  },
  {
    id: 'memory' as GameId,
    title: 'Memory Match',
    emoji: '🃏',
    description: 'Flip cards and find matching pairs. Test your memory!',
    difficulty: 'Medium',
    players: '1 Player',
    color: '#06B6D4',
    glow: 'rgba(6,182,212,0.3)',
    tags: ['Memory', 'Puzzle', 'Solo'],
  },
  {
    id: 'chess' as GameId,
    title: 'Chess',
    emoji: '♟️',
    description: 'The ultimate strategy game. Checkmate your opponent.',
    difficulty: 'Hard',
    players: '2 Players',
    color: '#F59E0B',
    glow: 'rgba(245,158,11,0.3)',
    tags: ['Strategy', 'Classic', 'Skill'],
  },
  {
    id: 'sudoku' as GameId,
    title: 'Sudoku',
    emoji: '🔢',
    description: 'Fill the 9×9 grid so every row, column and box has 1–9.',
    difficulty: 'Medium',
    players: '1 Player',
    color: '#10B981',
    glow: 'rgba(16,185,129,0.3)',
    tags: ['Logic', 'Numbers', 'Solo'],
  },
];

export default function GamesHubContent() {
  const [activeGame, setActiveGame] = useState<GameId>(null);

  if (activeGame === 'tictactoe') return <TicTacToeGame onBack={() => setActiveGame(null)} />;
  if (activeGame === 'memory') return <MemoryMatchGame onBack={() => setActiveGame(null)} />;
  if (activeGame === 'chess') return <ChessGame onBack={() => setActiveGame(null)} />;
  if (activeGame === 'sudoku') return <SudokuGame onBack={() => setActiveGame(null)} />;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">🎮</span>
          <h1 className="text-3xl font-bold text-foreground">Games Hub</h1>
        </div>
        <p style={{ color: 'var(--muted-foreground)' }}>
          Play casual games, earn XP, and climb the arcade leaderboard.
        </p>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Games Played', value: '47', icon: '🎯' },
          { label: 'Total Wins', value: '31', icon: '🏆' },
          { label: 'Win Rate', value: '66%', icon: '📈' },
          { label: 'XP Earned', value: '2,840', icon: '⚡' },
        ].map((stat) => (
          <div key={stat.label} className="rounded-xl p-4 flex items-center gap-3" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <span className="text-2xl">{stat.icon}</span>
            <div>
              <p className="text-xl font-bold text-foreground">{stat.value}</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Game cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {GAMES.map((game) => (
          <button
            key={game.id}
            onClick={() => setActiveGame(game.id)}
            className="text-left rounded-2xl p-6 transition-all duration-200 hover:scale-[1.02] group"
            style={{
              background: 'var(--card)',
              border: `1px solid var(--border)`,
              boxShadow: 'none',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 8px 32px ${game.glow}`;
              (e.currentTarget as HTMLButtonElement).style.borderColor = game.color;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
            }}
          >
            {/* Emoji */}
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl mb-4"
              style={{ background: `${game.glow}`, border: `1px solid ${game.color}33` }}>
              {game.emoji}
            </div>

            <h3 className="text-lg font-bold text-foreground mb-1">{game.title}</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>{game.description}</p>

            {/* Tags */}
            <div className="flex flex-wrap gap-1 mb-4">
              {game.tags.map((tag) => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: `${game.glow}`, color: game.color, border: `1px solid ${game.color}33` }}>
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex gap-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                <span>⚡ {game.difficulty}</span>
                <span>👥 {game.players}</span>
              </div>
              <span className="text-xs font-semibold px-3 py-1 rounded-full"
                style={{ background: game.color, color: '#fff' }}>
                Play
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
